/**
 * Licensing & Brand Partnerships — 10 documents.
 *
 * Every agreement here is the same trade made in different proportions: one side lends a name,
 * a face or a property, the other side makes and sells a physical product with it.
 *
 * Four mechanics decide whether the deal works, and they are the same four every time:
 *   money      — royalty base, advance and minimum guarantee, and what "net sales" actually deducts
 *   scope      — territory, channels, categories, exclusivity and term
 *   control    — approval rights over artwork, formula and marketing, and the quality obligations
 *                that come with them
 *   the ending — sell-off of finished inventory, committed packaging, and what each side keeps
 * In CPG the ending is the part founders under-negotiate and the part that costs the most, because
 * a licence that stops does not stop the cans that are already printed.
 */
export const LICENSING_DOCS = [
  {
    id: "trademark-license-agreement",
    name: "Trademark License Agreement",
    category: "licensing",
    subcategory: "Trademark and brand licensing",
    aliases: [
      "Trade Mark Licence",
      "Mark License Agreement",
      "Brand Mark Licensing Contract",
      "Licensing Agreement (Trademark)",
    ],
    stages: ["growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Licensor (mark owner) + Licensee (product maker)",
    whenYouNeedIt:
      "When someone else's name will appear on your product, or your name will appear on someone else's — a heritage brand extending into beverage, a restaurant name on a retail SKU, your own mark licensed into a category you will not make yourself.",
    whatItDoes:
      "Permits the licensee to use a trademark on defined products, in a defined territory and channel set, for a defined term, in exchange for royalties — and imposes the quality controls the licensor needs to keep the mark meaningful.",
    whyItMatters:
      "The economics and the operations pull in opposite directions and the contract has to hold both. On the money side, the royalty percentage is the number founders negotiate and the definition of the royalty base is the number that decides the outcome: whether trade spend, slotting, spoils, free fills, returns and freight come off before the royalty is calculated is worth more than a point or two of rate. On the operating side, the licensor's approval rights over artwork and product changes sit directly on top of a production calendar — a co-packer's line time is booked weeks out and label minimums have their own lead times, so an approval right with no response deadline is a right to stop your production run. And a minimum guarantee keeps accruing whether or not the product sells.",
    majorTerms: [
      "Licensed marks and the exact products they may appear on",
      "Territory and permitted channels",
      "Exclusive, sole or non-exclusive grant",
      "Royalty rate and the definition of the royalty base",
      "Deductions permitted before royalty is calculated",
      "Advance against royalties and whether it is recoupable",
      "Minimum guarantee, and whether it is per year or over the term",
      "Term, renewal conditions and performance thresholds",
      "Approval rights over artwork, packaging, formula and marketing",
      "Approval turnaround and deemed-approval mechanics",
      "Quality control standards, samples and facility inspection",
      "Royalty reporting cadence and audit rights",
      "Sell-off period and disposal of remaining inventory",
      "Termination triggers and consequences",
    ],
    clausesToWatch: [
      {
        clause: "Definition of net sales",
        why: "Whether trade spend, slotting, deductions, spoils, free fills and freight are deductible before the royalty is calculated moves more money than the rate itself.",
      },
      {
        clause: "Minimum guarantee",
        why: "It accrues on a calendar, not on performance, so a slow first year can leave a licensee paying royalties on sales that never happened.",
      },
      {
        clause: "Approval turnaround",
        why: "An approval right with no response deadline lets an unanswered email hold a booked co-packing run and a label print window.",
      },
      {
        clause: "Quality control and inspection",
        why: "The licensor has to police quality to keep the mark meaningful, so this obligation is not something a licensee can negotiate away — it can only be made workable.",
      },
      {
        clause: "Sell-off period",
        why: "Without it, finished goods, printed labels and committed packaging become unsellable the day the term ends.",
      },
      {
        clause: "Performance thresholds tied to exclusivity",
        why: "An exclusive licence with no minimum performance lets a licensee sit on a category, and one with unrealistic minimums converts a soft year into a termination right.",
      },
      {
        clause: "Audit rights and their cost allocation",
        why: "A royalty audit is only useful if the agreement says who pays when a shortfall is found and how far back the audit may reach.",
      },
      {
        clause: "Ownership of goodwill and derivative artwork",
        why: "Artwork created for the licensed product usually needs to sit with the mark owner, and a licensee who assumes otherwise loses assets it paid to develop.",
      },
    ],
    cpgIssues: [
      "Approval rights over artwork and formula changes land on a manufacturing calendar: co-packers book line time in advance, labels and cans carry minimum order quantities and lead times, and an approval that takes three weeks can push a run into the next available slot months later.",
      "Minimum guarantees keep accruing when a product underperforms, and in a category where distribution can be pulled at a single retailer reset, a guarantee set off an optimistic first-year forecast becomes a fixed liability against falling volume.",
      "Quality control is the licensor's mechanism for protecting the mark, and in food and beverage it usually means production samples, specification sign-off, facility certifications and the right to inspect — expect it, plan the lead time for it, and do not expect it to be waived.",
      "Sell-off periods decide what happens to finished inventory, printed labels and committed packaging when a licence ends, and the packaging commitment is often larger than the finished goods because of print minimums.",
      "Reformulation is not a licensee-only decision under most licences: an ingredient swap forced by a supplier shortage may need the licensor's approval, which turns a supply problem into a contractual one.",
      "Retailer deductions arrive months after the sale, so a royalty calculated on gross shipments rather than on net receipts can require paying royalties on revenue that was later deducted away.",
      "Recall responsibility needs its own allocation: the mark is on the pack, so a licensor has real exposure and a licensee usually carries the operational cost, and silence here is expensive during an incident.",
      "Royalty rates and guarantee structures vary widely by category, brand strength and exclusivity — treat any number you are quoted as a starting point specific to that deal, not as a market standard.",
    ],
    founderMistakes: [
      "Negotiating the royalty rate hard and accepting a net sales definition that allowed almost no deductions, then paying royalties on revenue lost to slotting and deductions.",
      "Agreeing an annual minimum guarantee off a launch forecast, losing a chain reset, and owing the guarantee on volume that never shipped.",
      "Accepting an approval right with no turnaround deadline and losing a booked co-packing slot while waiting for a licensor's creative team to respond.",
      "Signing without a sell-off period, then destroying finished inventory and a full label minimum on the termination date.",
      "Treating the licensor's quality obligations as bureaucracy and being surprised by a facility audit requirement mid-launch.",
      "Never modelling the guarantee against a realistic downside case, so the licence was profitable only in the forecast that justified it.",
    ],
    negotiationPoints: [
      "Negotiate the definition of net sales line by line, naming which deductions come off before the royalty is calculated.",
      "Tie any minimum guarantee to a downside case you have actually modelled, and ask for a cure or step-down rather than automatic termination on a miss.",
      "Set an approval turnaround with a deemed-approval consequence, and a fast-track path for changes forced by a supply or specification issue.",
      "Ask for a sell-off period long enough to clear both finished goods and committed packaging, with the mechanics stated.",
      "Agree quality-control mechanics in a practical form: sample submission points, who pays for testing, and how much notice an inspection needs.",
      "Ask for a royalty calculated on net receipts, or for a true-up when deductions land after the sale.",
      "Where the grant is exclusive, pair performance thresholds with a right to convert to non-exclusive rather than lose the licence outright.",
      "Settle recall cost allocation and insurance in the licence rather than leaving it to the supply chain documents.",
    ],
    redFlags: [
      "A royalty base defined as gross sales with no deductions in a category with heavy trade spend.",
      "A minimum guarantee with no cure period and automatic termination on a shortfall.",
      "Approval rights with no response deadline.",
      "No sell-off period, or one measured in days.",
      "Termination for convenience by the licensor with no compensation for committed inventory and packaging.",
      "An audit right reaching back indefinitely, with all costs on the licensee regardless of outcome.",
      "Silence on who carries recall cost when the mark is on the pack.",
    ],
    beforeYouSign: [
      "A full landed-cost and margin model with the royalty and guarantee built in, run against a downside volume case.",
      "The artwork and approval workflow mapped against your actual production and print lead times.",
      "Written confirmation of the licensor's registrations in every territory and category in scope.",
      "Your quality and specification documentation ready for the licensor's review process.",
    ],
    related: [
      "brand-license-agreement",
      "co-branding-agreement",
      "trademark-assignment",
      "international-trademark-license-agreement",
      "celebrity-license-agreement",
      "product-collaboration-agreement",
    ],
    attorneyReview: "yes",
    templateId: "trademark-license-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "brand-license-agreement",
    name: "Brand License Agreement",
    category: "licensing",
    subcategory: "Trademark and brand licensing",
    aliases: [
      "Brand Licensing Contract",
      "Brand Extension Agreement",
      "Master Brand Licence",
      "Trade Dress Licence",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand Owner + Licensee",
    whenYouNeedIt:
      "When the deal covers more than a mark — the whole brand identity, trade dress, positioning, recipes or asset library — because a licensee is building products under your brand, or you are building under someone else's.",
    whatItDoes:
      "Licenses a complete brand system rather than a single trademark: the marks, the trade dress, the brand guidelines, sometimes the formulations and the creative library, with the controls needed to keep the brand consistent across a maker you do not own.",
    whyItMatters:
      "A brand licence is a trademark licence with much more surface area, and the surface area is where it goes wrong. The licensee is now making decisions about pack design, flavour naming, price positioning and channel that all read to a consumer as the brand's decisions. If the brand guidelines are a reference document rather than a binding exhibit with an approval process behind them, the licensor has sold the right to define its own brand to someone whose incentives point at volume. For the licensee, the mirror risk is that a licence broad enough to be worth building a business on can be narrowed by an approval right exercised at the pack level, one SKU at a time.",
    majorTerms: [
      "Scope: marks, trade dress, guidelines, formulations, creative assets",
      "Product categories, formats and channels licensed",
      "Territory and exclusivity",
      "Rights the licensor retains for its own use",
      "Royalty structure, advance and minimum guarantee",
      "Brand guidelines as a binding exhibit and how they may be updated",
      "Approval process for products, packaging and marketing",
      "Quality standards, testing and audit rights",
      "Marketing commitments and minimum spend by each side",
      "Ownership of new assets created under the licence",
      "Customer and consumer data ownership",
      "Term, renewal, sell-off and post-term restrictions",
    ],
    clausesToWatch: [
      {
        clause: "Retained rights of the licensor",
        why: "A licensee needs to know exactly what the brand owner may still do in the same category and channel, or the exclusivity it paid for is partial.",
      },
      {
        clause: "Brand guidelines as a moving target",
        why: "Guidelines the licensor can update unilaterally can force repackaging on a licensee holding printed inventory.",
      },
      {
        clause: "Ownership of assets created under the licence",
        why: "New artwork, photography and formulations developed by the licensee usually need a clear owner, or both sides claim the same asset at the end.",
      },
      {
        clause: "Consumer and customer data",
        why: "Direct-to-consumer sales under a licensed brand generate data both sides want, and the default in most drafts is silence.",
      },
      {
        clause: "Marketing commitments",
        why: "A licence with no spend obligation lets a licensee ride the brand without building it, and one with a rigid obligation can outlast the licensee's ability to fund it.",
      },
      {
        clause: "Post-term restrictions",
        why: "What the licensee may sell after the term — including a near-identical product under its own name — decides whether the licence built a business or a competitor.",
      },
    ],
    cpgIssues: [
      "A brand licence usually crosses into formulation and specification, so the licence and the manufacturing documents have to agree on who owns the recipe and who may change it.",
      "Certification claims carried by the brand — organic, kosher, non-GMO, gluten-free and similar — have to be maintained at the licensee's facility and supply chain, and the certification is granted to a producer, not to a name.",
      "Trade dress consistency across a licensee's SKUs and the licensor's own SKUs matters at shelf, where a consumer sees both together and cannot tell which company made which.",
      "Price positioning by a licensee affects the whole brand: a licensed SKU discounted into a value channel can reset what retailers will pay for the licensor's own products.",
      "Channel carve-outs matter more in CPG than the category carve-outs founders focus on — club, dollar, foodservice and marketplace each behave differently and each can damage the others.",
      "A licensee's recall or quality incident lands on the brand name, so quality obligations, insurance and recall cost allocation belong in the licence and not only in the co-packing paperwork.",
    ],
    founderMistakes: [
      "Licensing a brand into an adjacent category with no channel restriction and finding the licensed product discounted in a channel that reset the whole brand's price expectations.",
      "Treating brand guidelines as a reference PDF rather than a binding exhibit, and losing control of pack design one SKU at a time.",
      "Never addressing ownership of the photography and artwork the licensee produced, and losing a strong asset library at the end of the term.",
      "Granting exclusivity in a category without listing the rights retained, then competing with the licensee through the brand's own direct channel.",
      "Failing to require insurance and recall cost allocation from a licensee whose quality incident became the brand's news story.",
    ],
    negotiationPoints: [
      "Attach brand guidelines as a dated exhibit and require notice, a transition window and cost allocation for any change.",
      "Write the retained-rights clause explicitly, naming channels and categories each side keeps.",
      "Allocate ownership of newly created artwork, photography and formulations up front, with a licence back where the creating side needs continued use.",
      "Address consumer data ownership and permitted use before any direct channel launches.",
      "Set marketing commitments as a percentage of net sales with a floor, rather than a fixed sum that outlives the plan.",
      "Define what the licensee may and may not sell after the term, including look-alike products.",
    ],
    redFlags: [
      "Guidelines the licensor may change at will with no transition period or cost sharing.",
      "No statement of retained rights alongside an exclusivity grant.",
      "Silence on ownership of assets created during the licence.",
      "No quality, insurance or recall provisions in a food or beverage licence.",
      "No post-term restriction on the licensee launching a near-identical product.",
    ],
    beforeYouSign: [
      "A written schedule of the marks, trade dress elements and assets actually being licensed.",
      "A channel map showing where each side may sell, agreed before exclusivity is priced.",
      "Certification and quality documentation for the producing facility.",
    ],
    related: [
      "trademark-license-agreement",
      "celebrity-license-agreement",
      "character-license-agreement",
      "co-branding-agreement",
      "product-collaboration-agreement",
      "brand-acquisition-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "celebrity-license-agreement",
    name: "Celebrity License Agreement",
    category: "licensing",
    subcategory: "People and personality licensing",
    aliases: [
      "Celebrity Endorsement Agreement",
      "Talent Licensing Agreement",
      "Personality Licence",
      "Founder-Partner Agreement (celebrity)",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Celebrity or their Loan-Out Company",
    whenYouNeedIt:
      "When a well-known individual's name, face or association will be attached to a product — on the pack, in the brand name, or as the public voice of a launch.",
    whatItDoes:
      "Licenses the individual's name, image, likeness and endorsement for defined products and media, sets what they must do and say, what the brand may do with the association, and how the relationship ends.",
    whyItMatters:
      "Celebrity deals in CPG carry a risk no other licence has: the morals clause and the printed pack. Packaging is committed months in advance, printed at minimums, and sits in inventory and on shelf for months more. If a conduct provision is triggered, the association you are contractually required to end is physically printed on product in distribution centres, in stores and in a co-packer's warehouse — and the cost of that moment is a full inventory and packaging write-off plus the lead time to reprint. The rest of the deal is a normal licence: guaranteed compensation versus royalty, days of service, approvals, exclusivity. The morals clause and the sell-off are the two that decide what happens on the worst day.",
    majorTerms: [
      "Rights licensed: name, image, likeness, voice, signature, biography",
      "Products and media covered, including packaging",
      "Compensation: guaranteed fee, royalty, equity or a combination",
      "Days of service: shoots, appearances, social posts, press",
      "Approval rights over creative, copy and product",
      "Category exclusivity and its precise scope",
      "Conduct and reputation provisions, and who decides they are triggered",
      "Term, renewal and post-term sell-off for printed inventory",
      "Disclosure of the paid relationship in the individual's own content",
      "Existing sponsorships, representation and conflicting obligations",
      "Insurance and who bears the cost of a forced repackaging",
    ],
    clausesToWatch: [
      {
        clause: "Conduct and reputation provisions",
        why: "This clause decides whether the brand can end the association and stop paying, and how quickly — and in CPG it also decides who pays for the packaging that has to come off shelf.",
      },
      {
        clause: "Sell-off for printed packaging",
        why: "A licence that ends without a wind-down leaves committed labels, printed cans and finished goods carrying an association you are no longer permitted to use.",
      },
      {
        clause: "Approval rights over product and creative",
        why: "Broad approvals with no deadline stall artwork and production; no approvals at all put words and images in front of an audience the individual will publicly disown.",
      },
      {
        clause: "Guaranteed compensation versus royalty",
        why: "A guarantee is a fixed liability that survives a product that does not sell, and it needs modelling against a downside case before it is agreed.",
      },
      {
        clause: "Existing obligations and representation",
        why: "An individual bound by a prior sponsor, a team, a network or a management agreement may not be free to grant everything they are granting.",
      },
      {
        clause: "Disclosure of the relationship",
        why: "Sponsored-content disclosure obligations attach to what the individual posts, and the brand generally carries responsibility for requiring them.",
      },
    ],
    cpgIssues: [
      "A face or a name on primary packaging is printed at minimum order quantities and sits in the supply chain for months, so the term, the sell-off and the conduct provisions all have to be written against the packaging cycle rather than the campaign calendar.",
      "Celebrities frequently want approval over flavour, formulation and ingredient choices, which puts a non-operator in the middle of a production schedule with booked line time.",
      "Athletes subject to competition testing usually require third-party certification of a supplement or energy product before associating with it, and that certification carries cost and lead time.",
      "Statements the individual makes about how a functional product works for them are claims about the product in practice, and the brand carries the exposure regardless of who said it.",
      "Retailer and marketplace listing images featuring an individual outlive campaigns, so the end of a term requires an asset refresh across channels you do not directly control.",
      "Where the celebrity takes equity as well as a fee, the endorsement licence and the equity documents have to be consistent about what happens to the association if they exit.",
    ],
    founderMistakes: [
      "Printing an individual's image on packaging under a short term and holding unsellable inventory the day the term ended.",
      "Agreeing a guaranteed fee structure with no downside modelling, and paying it in a year when the product lost distribution.",
      "Granting product and creative approval with no turnaround deadline, and missing a launch window waiting for a manager to respond.",
      "Signing without confirming existing sponsorship commitments, which unwound the deal after artwork was underway.",
      "Writing a conduct provision that required a conviction rather than a reputational event, leaving no practical ability to act.",
      "Never agreeing who pays for repackaging if the association has to end, and arguing about it during the incident.",
    ],
    negotiationPoints: [
      "Negotiate the conduct provision on standard and process: what triggers it, who decides, how fast the association stops and who bears the packaging cost.",
      "Secure a sell-off period sized to your actual printed inventory and packaging commitments, not a generic number of days.",
      "Set approval rights with deemed-approval deadlines and a fast-track path for supply-driven changes.",
      "Model the guaranteed compensation against a downside volume case before agreeing it.",
      "Require written confirmation that the individual is free of conflicting commitments and must notify you of new ones.",
      "Require certification of the product where the individual is subject to competition testing, and agree who pays for it.",
      "Make disclosure of the paid relationship a condition of payment for each posting obligation.",
    ],
    redFlags: [
      "Packaging use with no sell-off period.",
      "A conduct provision that runs only one way, or none at all on a multi-year deal.",
      "Unlimited approval rights with no response deadline.",
      "No representation about existing sponsorships or representation agreements.",
      "A guaranteed fee unconnected to any performance and payable in full regardless of distribution.",
      "An agreement signed only by a manager with no evidence of authority to bind the individual.",
    ],
    beforeYouSign: [
      "Written confirmation of the individual's existing sponsorships, representation and any governing-body obligations.",
      "A decision on whether packaging use is in scope, made before artwork is commissioned.",
      "A downside model showing what the guaranteed compensation costs if the product underperforms.",
      "Your packaging commitment schedule, so the sell-off period is negotiated against real print minimums and lead times.",
    ],
    related: [
      "nil-agreement",
      "nil-release",
      "brand-license-agreement",
      "trademark-license-agreement",
      "ambassador-agreement",
      "influencer-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Personality rights, endorsement disclosure and the consequences of a conduct-based termination all vary by jurisdiction, and packaging use makes an error expensive to unwind in printed inventory.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "character-license-agreement",
    name: "Character License Agreement",
    category: "licensing",
    subcategory: "Trademark and brand licensing",
    aliases: [
      "Entertainment Property Licence",
      "IP Property License Agreement",
      "Film or Game Tie-In Licence",
      "Character Merchandising Agreement",
    ],
    stages: ["growth"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Property Owner (studio, publisher or rights holder) + Brand",
    whenYouNeedIt:
      "When a character, film, game, sports property or illustrated IP will appear on your packaging or in a limited-edition SKU — usually against a release window you do not control.",
    whatItDoes:
      "Licenses a defined property for use on specified products, with a style guide, an approval chain, a territory tied to the property's own rights, and a term that is frequently anchored to a release or a season.",
    whyItMatters:
      "Character licensing runs on approvals, and the approval chain is longer than any founder expects: creative approval, brand approval, sometimes a co-owner and sometimes the talent whose likeness sits inside the character. Each stage adds days to an artwork cycle that in CPG is already up against label minimums, can lead times and a co-packer's booked line time. The second structural problem is the anchor date. Tie-in SKUs are built around a release, and releases slip; a product produced for a window that moves becomes seasonal inventory with no season. Add a minimum guarantee and an advance paid before any of it, and the downside is committed long before the upside is known.",
    majorTerms: [
      "The property, characters and specific assets licensed",
      "Products, formats and pack sizes covered",
      "Territory, tied to the property owner's own rights",
      "Term, and whether it is anchored to a release or season",
      "Advance, minimum guarantee and royalty rate",
      "Style guide and mandatory usage rules",
      "Approval stages, submission format and turnaround",
      "Restrictions on how the character may be shown",
      "Marketing commitments and permitted advertising",
      "Sell-off period after the term or the release window",
      "The property owner's right to withdraw or suspend the property",
    ],
    clausesToWatch: [
      {
        clause: "Approval chain and turnaround",
        why: "Multi-stage approvals with no deadlines sit directly on top of print lead times and booked production, and a slow round can cost a whole season.",
      },
      {
        clause: "Term anchored to a release date",
        why: "If the release moves, a term tied to it can expire around a product that has not had its moment, or leave you producing against a date that no longer exists.",
      },
      {
        clause: "Style guide restrictions",
        why: "Style guides commonly limit what a character may be shown doing — including holding or consuming a product — which can invalidate a concept after artwork has begun.",
      },
      {
        clause: "Advance and minimum guarantee",
        why: "Both are usually payable regardless of how the tie-in performs, and they are committed before you know whether the property will land.",
      },
      {
        clause: "Withdrawal or suspension rights",
        why: "A property owner able to pull the character for reputational or strategic reasons can strand printed packaging you have already committed to.",
      },
      {
        clause: "Sell-off after the window",
        why: "Tie-in inventory has a short commercial life, and without a workable sell-off it becomes a write-off rather than a discount.",
      },
    ],
    cpgIssues: [
      "Approval rounds have to be scheduled against label and can lead times and a co-packer's booked line time — a three-week approval cycle can push production past the release window entirely.",
      "Products marketed to children carry additional expectations from both retailers and the property owner about ingredients, claims and how the product is presented, and those constraints belong in the conversation before the concept is designed.",
      "Style guides frequently prohibit showing a character consuming or endorsing a product, which is often exactly the idea a brand team brings to the first meeting.",
      "Tie-in SKUs are usually produced as a single run at minimum order quantities, so a slipped release date turns a full run of printed packaging into dead inventory.",
      "Seasonal and event-anchored packaging has no residual value: unlike a normal label revision, unsold tie-in stock cannot be sold through after the moment passes.",
      "Retailers commit shelf space for tie-in items against the release calendar, so a delay costs the distribution as well as the inventory.",
      "Advances and guarantees are usually paid before the co-packing deposit, which stacks two large cash commitments ahead of any revenue.",
    ],
    founderMistakes: [
      "Committing to a full label minimum before final artwork approval, then having a style guide note force a redesign after the labels were printed.",
      "Building a tie-in around a release date that moved by four months, leaving a produced run with no season to sell into.",
      "Paying an advance and a guarantee before testing whether retailers would actually take the tie-in SKU.",
      "Designing a concept showing the character drinking the product, and losing it to a style guide restriction after the design work was done.",
      "Underestimating the approval chain and losing a shelf reset window while waiting on a second-stage sign-off.",
    ],
    negotiationPoints: [
      "Negotiate approval turnaround times with deemed-approval consequences, and get the style guide before artwork begins.",
      "Ask for the term to extend automatically if the anchor release date moves, rather than expiring against a date you do not control.",
      "Structure the guarantee in stages tied to confirmed retail distribution rather than paying it all up front.",
      "Secure a sell-off period sized to the real production run and to retail sell-through, not to a generic period.",
      "Ask for compensation or a credit if the property is withdrawn or suspended after you have committed to packaging.",
      "Confirm the property owner's rights in every territory and every channel in scope, including marketplaces.",
    ],
    redFlags: [
      "Approval rights with no turnaround commitment on a product with print lead times.",
      "A term that expires on a fixed date regardless of whether the anchor release moves.",
      "An advance and guarantee payable in full before any retail commitment exists.",
      "A withdrawal right with no compensation for committed packaging.",
      "A style guide that is referenced but not provided before signature.",
    ],
    beforeYouSign: [
      "The style guide and the full approval workflow, read before any concept work starts.",
      "Your production and print lead times mapped against the approval cycle and the release date.",
      "Confirmed retailer interest in the tie-in SKU before the advance is committed.",
    ],
    related: [
      "trademark-license-agreement",
      "brand-license-agreement",
      "packaging-design-agreement",
      "packaging-artwork-ownership",
      "co-branding-agreement",
      "product-collaboration-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "nil-agreement",
    name: "Name / Image / Likeness Agreement",
    category: "licensing",
    subcategory: "People and personality licensing",
    aliases: [
      "NIL Deal",
      "Athlete Endorsement Agreement",
      "Personality Rights Agreement",
      "Creator NIL Agreement",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Athlete, Creator or Public Figure",
    whenYouNeedIt:
      "When you are paying an individual for the commercial use of their identity and for endorsement activity — not just permission to use a photograph, which is a release, but a full commercial deal.",
    whatItDoes:
      "Sets the complete commercial relationship: what identity elements are licensed, what the individual must deliver, what they are paid, what the brand may approve, what conduct terms apply, and what happens to content and packaging at the end.",
    whyItMatters:
      "Founders reach for a short release when the deal is really an endorsement, and the release is silent on everything that matters: compensation structure, exclusivity, approval over how the product is described, conduct terms, and a sell-off for anything printed. For a supplement, energy drink or functional beverage the stakes are higher again. The individual's own obligations — to a team, a school, a league, a collective or an existing sponsor — may limit what they are able to grant, and those rules vary and change; that is a question for counsel before the deal is priced, not after the first post.",
    majorTerms: [
      "Identity elements licensed: name, image, likeness, voice, signature, nickname",
      "Deliverables: posts, appearances, shoots, content, days of service",
      "Compensation: fee, royalty, affiliate share, product or equity",
      "Media and channels, and whether packaging is included",
      "Territory and term",
      "Category exclusivity and its precise definition",
      "Approval rights over creative and over how the product is described",
      "Required disclosure of the paid relationship",
      "Conduct and reputation provisions",
      "Representations about existing obligations and freedom to contract",
      "Product certification requirements where the individual is tested",
      "Post-term sell-off and content wind-down",
    ],
    clausesToWatch: [
      {
        clause: "Freedom to contract",
        why: "Team, school, league, collective or prior sponsor obligations can limit what the individual may grant, and the representation is the brand's only protection.",
      },
      {
        clause: "Approval over product description",
        why: "The brand carries exposure for what is said about a functional product, so review of the individual's language is a control rather than a courtesy.",
      },
      {
        clause: "Category exclusivity definition",
        why: "In beverage and supplements the categories are granular, and an undefined category leaves the individual free to appear for something you would consider a direct competitor.",
      },
      {
        clause: "Conduct provisions",
        why: "This is what lets the brand stop the association and stop paying, and it needs a defined process rather than a vague standard.",
      },
      {
        clause: "Certification for tested individuals",
        why: "Competition testing regimes can make an association conditional on third-party product certification, which has real cost and lead time.",
      },
      {
        clause: "Post-term wind-down",
        why: "Content stays live and packaging stays printed after a term ends unless the agreement says who takes it down and by when.",
      },
    ],
    cpgIssues: [
      "Athletes subject to competition testing commonly require third-party certification of a supplement or energy product before associating with it, and the certification has cost, lead time and ongoing obligations at the manufacturing facility.",
      "An individual's account of what a product does for them functions as a claim about the product, and for supplements and functional beverages that is where the brand's exposure concentrates.",
      "Packaging featuring an individual is printed months ahead and sits in the supply chain for months after, so both the term and the sell-off have to cover the whole cycle.",
      "Category exclusivity in beverage is granular — energy, sports hydration, functional soda, protein and recovery can be one category or five, and the definition drives the price.",
      "Marketplace and retailer listing images featuring the individual outlive the campaign, and refreshing them at the end of a term means coordinating with accounts you do not control.",
      "Product supplied to an individual and their team is inventory at landed cost, and open-ended supply commitments quietly become a real line item.",
    ],
    founderMistakes: [
      "Using a short release for what was functionally an endorsement, with no approval rights when the individual described the product in terms the brand could not support.",
      "Printing an individual on packaging under a one-year term and holding unsellable inventory when it ended.",
      "Signing with someone already committed in the category through a team or collective arrangement, which unwound the deal.",
      "Skipping product certification for a tested athlete and having the partnership stop before the first deliverable.",
      "Leaving conduct provisions out entirely on a multi-year deal with packaging exposure.",
      "Agreeing open-ended product supply with no allowance, and shipping several thousand units a year unbudgeted.",
    ],
    negotiationPoints: [
      "Use a full agreement rather than a release wherever money, exclusivity or packaging is involved.",
      "Require a written representation of freedom to contract, with notice of any new commitment during the term.",
      "Define category exclusivity by listing included and excluded product types rather than by a single word.",
      "Set approval rights over how the product is described, with a deemed-approval deadline so campaigns are not held up.",
      "Negotiate a sell-off period sized to the actual printed inventory and packaging commitment.",
      "Agree who pays for third-party product certification and by when, before any deliverable date is set.",
      "Make disclosure of the paid relationship a condition of payment for each posting obligation.",
    ],
    redFlags: [
      "Packaging use with no sell-off period.",
      "No representation about existing team, school, league, collective or sponsor obligations.",
      "No conduct provision on a multi-year or packaging-bearing deal.",
      "No approval over product description on a supplement or functional beverage.",
      "An agreement signed by an agent with no evidence of authority.",
      "Open-ended product supply with no cap.",
    ],
    beforeYouSign: [
      "Written confirmation of the individual's existing obligations and any rules that limit what they may grant, reviewed by counsel.",
      "A decision on whether packaging use is in scope, before artwork is commissioned.",
      "Confirmation of any product certification required, with cost, lead time and who pays.",
    ],
    related: [
      "nil-release",
      "celebrity-license-agreement",
      "ambassador-agreement",
      "influencer-agreement",
      "model-release",
      "brand-ambassador-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Personality rights, endorsement disclosure and the rules governing what an athlete or student athlete may grant differ by jurisdiction, sport and institution and change frequently.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "co-branding-agreement",
    name: "Co-Branding Agreement",
    category: "licensing",
    subcategory: "Partnerships and collaborations",
    aliases: [
      "Co-Brand Partnership Agreement",
      "Dual Branding Agreement",
      "Joint Product Agreement",
      "Brand Collaboration Contract",
    ],
    stages: ["growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand A + Brand B",
    whenYouNeedIt:
      "When two brands put both names on one product — a collaboration SKU, a limited edition, an ingredient partnership where a second brand's component is called out on your pack.",
    whatItDoes:
      "Sets who makes the product, whose formula and artwork it uses, whose vendor number it sells under, how the economics split, who approves what, and how the whole thing unwinds when the collaboration ends.",
    whyItMatters:
      "Co-branding looks like a marketing exercise and behaves like a manufacturing joint venture. One brand's co-packer runs the product, which means the other brand's formula, specification and artwork now sit inside a facility it does not control and did not vet. One brand's name is on the vendor agreement, so the deductions, the chargebacks and the recall obligations land on that side regardless of whose idea it was. Both brands hold approval rights over artwork and quality, and both sets of approvals have to clear one production window. When it ends, someone is holding finished goods and a committed packaging minimum for a product that neither party can sell alone.",
    majorTerms: [
      "Which marks appear, in what hierarchy, on what",
      "Who manufactures and at whose facility",
      "Whose formula and specification the product uses",
      "Ownership of the artwork and of anything newly created",
      "Which party is the seller of record and holds the retail vendor relationship",
      "Economics: cost sharing, margin split or royalty",
      "Approval rights for both parties, with turnaround times",
      "Quality standards, testing and facility requirements",
      "Recall responsibility and cost allocation",
      "Insurance and indemnity between the two brands",
      "Volume commitments and who owns unsold inventory",
      "Term, exclusivity, renewal and sell-off",
    ],
    clausesToWatch: [
      {
        clause: "Manufacturer of record and label responsibility",
        why: "The party named on the pack carries the contact and responsibility that goes with it, and that party's obligations differ from the partner's whatever the marketing split says.",
      },
      {
        clause: "Recall responsibility",
        why: "Both names are on the pack but the operational and financial burden usually falls on one side, and silence here is discovered mid-incident.",
      },
      {
        clause: "Dual approval on artwork and specification",
        why: "Two approval chains against one booked production window is the most common reason a collaboration misses its date.",
      },
      {
        clause: "Ownership of unsold inventory",
        why: "A collaboration SKU cannot be sold by either party alone, so leftover stock and committed packaging need an owner named in advance.",
      },
      {
        clause: "Formula and specification confidentiality",
        why: "One partner's formula ends up in the other partner's co-packer, and the flow-down obligations have to be written rather than assumed.",
      },
      {
        clause: "Exclusivity and the right to do it again",
        why: "Whether either brand may run a similar collaboration with a competitor during or after the term decides how much the partnership is worth.",
      },
    ],
    cpgIssues: [
      "The collaboration runs at one party's co-packer, so the other party's formula, specification and artwork enter a facility it did not audit and has no contract with — flow-down confidentiality and quality obligations have to be built in deliberately.",
      "Certification and dietary claims have to survive the combination: if one brand's product is certified organic, kosher, gluten-free or non-GMO, the combined product only carries that claim if the whole formulation and facility support it.",
      "Allergen profiles combine, so a collaboration can introduce an allergen into a product whose customers chose it precisely because that allergen was absent.",
      "Whoever holds the retail vendor number takes the deductions, the chargebacks, the spoils and the free fills, and the collaboration economics have to net those out or one side quietly absorbs them.",
      "Both brands hold approval rights over the same artwork, and label minimums plus co-packer scheduling mean a single extra approval round can move a launch by a full production cycle.",
      "Shelf life is set by the shortest-lived component, and a collaboration between a shelf-stable product and a shorter-code ingredient inherits the shorter code.",
      "Committed packaging is usually the larger exposure at the end: print minimums on a collaboration label mean unsold film or labels outlast the unsold product.",
      "Lot coding and traceability need to work across both parties' systems so a recall can be executed down to a single run.",
    ],
    founderMistakes: [
      "Splitting margin on a collaboration and never accounting for the deductions that hit the party holding the vendor relationship, so one side made far less than the model said.",
      "Sending a formula to the partner's co-packer with no confidentiality flow-down and no audit of the facility.",
      "Launching a collaboration whose combined formulation quietly broke a certification claim that both brands carried separately.",
      "Building a dual approval process with no turnaround deadlines and missing the retail window the collaboration was designed for.",
      "Never agreeing who owned unsold inventory, and being left with pallets of a product only two brands together could sell.",
      "Committing to a label minimum sized for an optimistic forecast, and writing off most of the run.",
    ],
    negotiationPoints: [
      "Name the manufacturer of record, the seller of record and the vendor relationship explicitly, and price the deduction exposure into the split.",
      "Agree recall responsibility, insurance and indemnity between the brands before production is scheduled.",
      "Set a joint approval process with a single submission point and deemed-approval deadlines on both sides.",
      "Require confidentiality and quality flow-down to whichever co-packer runs the product, with both brands as beneficiaries.",
      "Decide who owns unsold finished goods and committed packaging, and cap the packaging commitment to a tested forecast.",
      "Agree what each brand may do with the collaboration artwork and any new formulation after the term.",
      "Confirm in writing that every certification and dietary claim on the pack holds for the combined product at the producing facility.",
    ],
    redFlags: [
      "No allocation of recall responsibility or insurance between the brands.",
      "Dual approval rights with no turnaround deadlines against a booked production run.",
      "Silence on who owns unsold inventory and committed packaging.",
      "A margin split calculated before deductions in a channel where deductions are normal.",
      "No confidentiality flow-down to the co-packer running the collaboration.",
      "No agreement on what either brand may sell after the term.",
    ],
    beforeYouSign: [
      "A joint production and print timeline with both approval chains mapped onto it.",
      "Written confirmation that every claim and certification on the pack holds for the combined product at the producing facility.",
      "A shared landed-cost and deduction model, agreed by both sides before the split is set.",
      "The co-packer's confidentiality and quality obligations to whichever brand is not their customer.",
    ],
    related: [
      "collaboration-agreement",
      "product-collaboration-agreement",
      "trademark-license-agreement",
      "cross-marketing-agreement",
      "co-packer-agreement",
      "recall-responsibility-agreement",
    ],
    attorneyReview: "yes",
    templateId: "co-branding-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "collaboration-agreement",
    name: "Collaboration Agreement",
    category: "licensing",
    subcategory: "Partnerships and collaborations",
    aliases: [
      "Joint Venture Lite Agreement",
      "Partnership Framework Agreement",
      "Working Together Agreement",
      "Joint Project Agreement",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Collaborating Party",
    whenYouNeedIt:
      "When two parties will work together on something without forming a company — a joint product, a shared campaign, a bundle, a pop-up — and need to settle contributions, costs, decisions and ownership before the work starts.",
    whatItDoes:
      "Frames the collaboration: what each side brings, what each side pays, who decides what, who owns what is created, how any money is split, and how either side leaves.",
    whyItMatters:
      "The hardest question in a collaboration is not the money, it is the ownership of what gets made. Two parties who jointly develop a flavour, a formulation or a creative property and never allocate ownership end up with an asset neither can commercialise cleanly and both feel entitled to. Joint ownership sounds fair and behaves badly: in practice it means every future use needs the other side's cooperation, years after the enthusiasm has gone. Allocate ownership to one party with a licence back to the other, decide it before the work starts, and the rest of the collaboration is just scheduling.",
    majorTerms: [
      "What each party contributes: IP, product, capital, distribution, audience",
      "Cost sharing and who fronts what",
      "Ownership of jointly created IP, and any licence back",
      "Decision rights and how deadlocks are broken",
      "Revenue or margin split and how it is calculated",
      "Use of each party's marks and approval process",
      "Exclusivity, if any, and its scope",
      "Confidentiality of formulations, costs and plans",
      "Term, exit rights and notice",
      "What each party may do with the output after the collaboration ends",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of jointly created IP",
        why: "Joint ownership requires cooperation for every future use, so allocating ownership to one side with a licence back is usually the workable answer.",
      },
      {
        clause: "Decision rights and deadlock",
        why: "Without a tie-breaker, a collaboration between two equal parties stalls on the first genuine disagreement, usually about spend.",
      },
      {
        clause: "Cost overruns",
        why: "Production, print and freight overruns are normal in CPG, and an agreement silent on who funds them turns a small variance into a dispute.",
      },
      {
        clause: "Post-term use of the output",
        why: "Whether either party may keep selling the collaboration product, or something close to it, decides what the collaboration was actually worth.",
      },
      {
        clause: "Nature of the relationship",
        why: "A loosely worded profit-sharing arrangement can be read as creating obligations neither side intended, which is worth having counsel look at.",
      },
      {
        clause: "Confidentiality of formulations and costs",
        why: "Collaborations involve sharing landed cost and specification detail with a party that may be a future competitor.",
      },
    ],
    cpgIssues: [
      "A jointly developed flavour or formulation is the asset most likely to be fought over, and the flavour house's own rights in the base may sit underneath both parties' claims.",
      "A bundle or multi-pack is its own SKU with its own item code, its own specification and its own kitting cost at the warehouse — treating it as a marketing idea rather than a product creates operational surprises.",
      "Whoever holds the inventory carries the risk on it, so the party fronting production needs the other side's commitment documented rather than assumed.",
      "Retail and marketplace listings for a collaboration item need one owner, because two parties editing the same listing is an operational problem as well as a commercial one.",
      "Shared campaigns disclose each side's promotional calendar and pricing, which is confidential to their retail partners as well as to them.",
      "If the collaboration involves co-manufacturing, the co-packer's own confidentiality and quality obligations only run to its customer unless flow-down is arranged.",
    ],
    founderMistakes: [
      "Developing a flavour jointly, never allocating ownership, and being unable to relaunch it without a former partner's consent.",
      "Splitting revenue with no definition of revenue, and arguing about whether it was before or after deductions and freight.",
      "Fronting a production run on a verbal commitment from a partner who then reduced their order.",
      "Building a bundle without setting it up as a distinct item, and creating an inventory and fulfilment mess at the warehouse.",
      "Running a joint campaign with no approval process, and having a partner publish pricing that undercut a retail account.",
    ],
    negotiationPoints: [
      "Allocate ownership of anything jointly created to one party, with a defined licence back to the other.",
      "Define the revenue or margin split precisely, naming what is deducted before it is calculated.",
      "Agree a decision process, including who breaks a deadlock and who approves spend above a threshold.",
      "Document each side's volume or funding commitment before production is scheduled.",
      "Set out explicitly what each party may do with the output and with any developed formulation after the term.",
      "Have counsel confirm the arrangement is documented as what you intend it to be rather than something broader.",
    ],
    redFlags: [
      "Joint ownership of IP with no mechanism for either side to use it independently.",
      "A revenue split with no definition of the base.",
      "No decision or deadlock mechanism between two equal parties.",
      "No confidentiality provision covering formulations and cost data.",
      "Silence on what happens to the collaboration product after the term.",
    ],
    beforeYouSign: [
      "A written statement of what each side is contributing and what each side expects to own.",
      "A costed plan including production, packaging, freight and a realistic overrun allowance.",
    ],
    related: [
      "co-branding-agreement",
      "product-collaboration-agreement",
      "cross-marketing-agreement",
      "promotional-partnership-agreement",
      "formula-ownership-agreement",
      "product-development-ip-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promotional-partnership-agreement",
    name: "Promotional Partnership Agreement",
    category: "licensing",
    subcategory: "Partnerships and collaborations",
    aliases: [
      "Joint Promotion Agreement",
      "On-Pack Promotion Agreement",
      "Tie-In Promotion Contract",
      "Partner Promotion Agreement",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Promotional Partner",
    whenYouNeedIt:
      "When two brands run a promotion together — an on-pack offer, an insert card, a bundled sample, a joint giveaway, a cross-brand discount — usually with a fixed start date and printed material behind it.",
    whatItDoes:
      "Sets what each party does in the promotion, who funds it, who fulfils it, whose marks appear where, and what happens if one side cannot deliver its half.",
    whyItMatters:
      "The moment a promotion touches the pack it stops being a marketing plan and becomes a production commitment. An on-pack offer means new artwork, a new print run at minimum order quantity, and a co-packer's line time booked against a date. If the partner's side slips — the offer is not ready, the landing page is late, the partner's product is out of stock — you are holding printed packaging that promotes something that does not exist, and there is no version of that fix which is cheap. The second issue is fulfilment: someone honours the offer, someone eats the margin, and someone answers the consumer who could not redeem it.",
    majorTerms: [
      "Promotion mechanic and its start and end dates",
      "Where each brand's marks appear and approval of the artwork",
      "Which party funds which element",
      "Who fulfils the offer and who bears the redemption cost",
      "Redemption caps and what happens when one is hit",
      "Print, production and inventory commitments on each side",
      "Consumer support and who handles complaints",
      "Rules for any element involving a prize or a draw",
      "Data captured during the promotion and who may use it",
      "Failure to deliver: remedies and cost allocation",
      "Term, extension and what happens to unsold promotional stock",
    ],
    clausesToWatch: [
      {
        clause: "Partner delivery failure",
        why: "Printed on-pack promotion is committed long before the partner has to perform, and the remedy for their failure has to cover your packaging cost.",
      },
      {
        clause: "Redemption caps and liability",
        why: "An uncapped offer is an open-ended liability, and a cap with no communication plan produces a stream of consumers who cannot redeem.",
      },
      {
        clause: "Fulfilment responsibility",
        why: "Whoever fulfils carries the cost and the customer service load, and both are routinely underestimated at the planning stage.",
      },
      {
        clause: "Promotion rules",
        why: "Anything with a prize or a draw element carries rules requirements that vary by jurisdiction, and both brands are associated with the outcome.",
      },
      {
        clause: "Data captured during the promotion",
        why: "Entrants and redeemers are consumer records, and what each brand may do with them afterwards is narrower than most partners assume.",
      },
      {
        clause: "Unsold promotional inventory",
        why: "Product carrying a dated on-pack offer is hard to sell after the date, and someone has to own the write-down.",
      },
    ],
    cpgIssues: [
      "An on-pack promotion is a label change: new artwork, a new print run at minimum order quantity, and a production slot — the promotion timeline is really the packaging timeline plus the partner's readiness.",
      "Dated on-pack offers strand inventory, because product printed with an expired offer sits in a distribution centre and reaches shelf months later.",
      "Insert cards have to survive the pack environment — moisture, cold, freight — and they have to be inserted, which means a line change and a cost per unit at the co-packer.",
      "Sample swaps put your product into a partner's box with no control over temperature, transit time or the allergen statement on the outer, which matters for anything chilled or allergen-sensitive.",
      "Retailers often need to approve on-pack promotions in advance, and a promotion the buyer has not seen can be refused at the door or pulled from the set.",
      "Redemption spikes hit fulfilment and inventory, so the promotion needs a stock plan on both sides rather than a marketing forecast.",
      "A partner's claim about your product in their promotional material becomes your problem, so approval over how each brand describes the other belongs in the agreement.",
    ],
    founderMistakes: [
      "Printing an on-pack offer at label minimum and having the partner's landing page arrive six weeks late, with product already at the distribution centre.",
      "Running an uncapped offer and discovering the redemption cost had no ceiling.",
      "Swapping samples with a partner whose fulfilment shipped a chilled product ambient in summer.",
      "Never agreeing who handled the consumers who could not redeem, and having them arrive on the brand's own channels.",
      "Launching an on-pack promotion a retailer had not approved and losing the set at that account.",
    ],
    negotiationPoints: [
      "Tie the on-pack commitment to written confirmation that the partner's side is live and ready before the print order is placed.",
      "Agree a remedy that covers packaging and inventory cost if the partner cannot deliver.",
      "Cap redemption, and agree the communication plan for what happens when the cap is reached.",
      "Name the fulfilment party and allocate the cost per redemption explicitly.",
      "Require counsel-reviewed written rules before anything with a prize element launches.",
      "Agree data ownership and permitted use for anyone who enters or redeems.",
      "Get retailer approval for any on-pack mechanic before committing artwork.",
    ],
    redFlags: [
      "An on-pack commitment with no remedy for partner failure.",
      "An uncapped offer with no ceiling on redemption cost.",
      "No named fulfilment party.",
      "A prize element with no written rules.",
      "No approval over how the partner describes your product.",
    ],
    beforeYouSign: [
      "The packaging and print timeline, with the partner's readiness date confirmed in writing before artwork is committed.",
      "A costed redemption model including the cap, the fulfilment cost and the customer service load.",
    ],
    related: [
      "co-branding-agreement",
      "cross-marketing-agreement",
      "collaboration-agreement",
      "contest-rules",
      "product-giveaway-rules",
      "sponsorship-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "cross-marketing-agreement",
    name: "Cross-Marketing Agreement",
    category: "licensing",
    subcategory: "Partnerships and collaborations",
    aliases: [
      "List Swap Agreement",
      "Co-Marketing Agreement",
      "Audience Sharing Agreement",
      "Cross-Promotion Contract",
    ],
    stages: ["launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Marketing Partner",
    whenYouNeedIt:
      "When two brands promote each other to their own audiences — newsletter features, social swaps, joint content, a shared landing page, discount codes carried in each other's post-purchase flows.",
    whatItDoes:
      "Sets what each side sends, to whom, when, with what creative approval, what data if any moves between the parties, and how each brand's marks and claims may be used by the other.",
    whyItMatters:
      "Cross-marketing looks like the lowest-risk partnership in this category and carries the one exposure that is not commercial. The moment subscriber data moves between two brands — a list swap, an uploaded audience, a shared entrant file — you are handling other people's personal information under consents they gave to one brand, not two. The rules governing that vary by jurisdiction, change often, and do not care that the partnership was informal. The commercial risks are smaller but real: a partner describing your supplement or functional beverage in their email is making claims you carry, and a discount code carried in someone else's flow can be redeemed at a margin nobody modelled.",
    majorTerms: [
      "What each party sends, to which audience, and when",
      "Whether any data moves between the parties, and in what form",
      "Consent basis for any audience sharing or upload",
      "Creative approval and required disclosures",
      "Approved language about each other's products",
      "Discount codes, tracking and who honours redemption",
      "Margin treatment of partner-driven orders",
      "Attribution, reporting and measurement",
      "Use of each other's marks and assets",
      "Term, exclusivity if any, and how either side exits",
    ],
    clausesToWatch: [
      {
        clause: "Data sharing and consent",
        why: "Subscribers consented to hear from one brand, and moving or uploading their records to another is the part of this agreement that needs legal review rather than a marketing decision.",
      },
      {
        clause: "Approved claim language",
        why: "A partner writing their own copy about your functional product can assert things you cannot support, and the association is with your brand.",
      },
      {
        clause: "Discount code economics",
        why: "A code that travels through a partner's audience can be shared publicly and redeemed at volume, at a margin the promotion never modelled.",
      },
      {
        clause: "Attribution and measurement",
        why: "Without an agreed method, both sides report the same orders and neither can tell whether the partnership did anything.",
      },
      {
        clause: "Send commitments and timing",
        why: "One side sending on schedule while the other quietly does not is the most common way these arrangements fail.",
      },
      {
        clause: "Exit and continued use of assets",
        why: "Landing pages, codes and creative featuring a former partner keep working after the relationship ends unless someone takes them down.",
      },
    ],
    cpgIssues: [
      "A partner's audience may sit where your SKU is not distributed or where you cannot ship a consumable, so the promotion generates demand and support load with no revenue.",
      "Partner copy describing a supplement or functional beverage carries your exposure, so approved language and a do-not-say list are worth more here than an approval right over layout.",
      "Discount codes shared in a partner's flow leak to deal sites, and a code with no cap or expiry can be redeemed at a margin that turns the partnership negative.",
      "Bundling a partner's product with yours turns into a kitting job at the warehouse with a per-unit cost, its own item code and its own inventory risk.",
      "Sending a partner's physical product alongside yours means their allergen and handling information rides in your box, and your customers will treat it as your responsibility.",
      "Post-purchase inserts promoting a partner are a print run with minimums, and they age out the moment the partner's offer or packaging changes.",
    ],
    founderMistakes: [
      "Swapping subscriber lists on a handshake and only afterwards asking what consent those subscribers had actually given.",
      "Letting a partner write their own copy about a functional product and having claims go out that the brand could not support.",
      "Issuing an uncapped discount code that reached a deal site and was redeemed thousands of times below margin.",
      "Committing to a send calendar the partner never honoured, having already sent to their audience.",
      "Leaving a co-branded landing page live for a year after the partnership ended.",
    ],
    negotiationPoints: [
      "Keep audiences separate: promote to your own list on the partner's behalf rather than transferring or uploading records, unless counsel has confirmed the basis for doing more.",
      "Supply approved language and a do-not-say list, and take approval over any copy describing your product.",
      "Cap and date every discount code, and agree who absorbs the margin on partner-driven orders.",
      "Agree an attribution method and a reporting format before the first send.",
      "Commit both sides to a written send calendar with a remedy for a missed send.",
      "Agree a takedown obligation for pages, codes and creative when the arrangement ends.",
    ],
    redFlags: [
      "List transfer or audience upload with no review of the consent basis.",
      "No approval right over how a partner describes your product.",
      "Uncapped, undated discount codes.",
      "No agreed attribution method.",
      "No takedown obligation at the end of the term.",
    ],
    beforeYouSign: [
      "Counsel's view on any transfer, upload or sharing of subscriber or customer records.",
      "Approved copy and a do-not-say list for anything the partner will publish about your product.",
      "A margin model for partner-driven orders including the discount and the fulfilment cost.",
    ],
    related: [
      "promotional-partnership-agreement",
      "co-branding-agreement",
      "collaboration-agreement",
      "email-marketing-consent",
      "privacy-policy",
      "affiliate-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Sharing or uploading subscriber and customer records between two brands engages marketing-consent and privacy rules that differ by jurisdiction and change often, and the consents were given to one brand rather than both.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-collaboration-agreement",
    name: "Product Collaboration Agreement",
    category: "licensing",
    subcategory: "Partnerships and collaborations",
    aliases: [
      "Limited Edition Agreement",
      "Collab SKU Agreement",
      "Chef Collaboration Agreement",
      "Artist Collaboration Agreement",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Collaborator (chef, artist, athlete or another brand)",
    whenYouNeedIt:
      "When a named collaborator shapes an actual product — a chef developing a flavour, an artist designing a can, another brand contributing an ingredient — and their name goes on the pack.",
    whatItDoes:
      "Covers the development and the launch of a specific collaboration SKU: who contributes what, who owns the recipe and the artwork afterwards, how the collaborator is paid, and what each side may do once the run is sold through.",
    whyItMatters:
      "The collaboration SKU is the fun part and the ownership question is the part nobody raises until it works. If the collaboration sells out, both sides want to repeat it, extend it or take the idea elsewhere — and what they may actually do is decided by a document written when everyone was optimistic. The recipe a chef developed, the artwork an artist drew and the flavour direction that came out of a joint session all need an owner named in advance, with a licence back where the other side needs continued use. The commercial exposure is more ordinary: a limited-edition run is a full minimum order of packaging against a forecast with no sales history, and the collaborator's fee is usually payable whether or not it sells through.",
    majorTerms: [
      "The product, format and run size",
      "The collaborator's contribution and their deliverables",
      "Compensation: fee, royalty, revenue share or product",
      "Ownership of the recipe, formulation and artwork",
      "Licence back to the collaborator, if any",
      "Approval rights over the product, artwork and marketing",
      "Use of the collaborator's name, image and likeness",
      "Marketing and appearance obligations on both sides",
      "Exclusivity: what each side may do in the category during and after",
      "Who funds and owns the production run and any unsold inventory",
      "Term, sell-off and rights to repeat or extend",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of the developed recipe",
        why: "A flavour developed jointly and left unallocated cannot be cleanly relaunched by either side, and the argument arrives exactly when it has proven itself.",
      },
      {
        clause: "Rights to repeat or extend",
        why: "Whether a successful collaboration can be run again, and on what terms, should be decided while both sides still think it might not work.",
      },
      {
        clause: "Ownership of the artwork",
        why: "Collaboration artwork is often the most distinctive asset produced, and an artist's default position is a licence for one run.",
      },
      {
        clause: "Who funds the run and owns unsold stock",
        why: "A limited edition is a full packaging minimum against an untested forecast, and the write-down needs an owner named in advance.",
      },
      {
        clause: "Post-term category restrictions",
        why: "A collaborator free to make a near-identical product with a competitor next season turns your launch into their proof of concept.",
      },
      {
        clause: "Name, image and likeness scope",
        why: "The collaborator's identity on the pack is a licence with its own term, and printed packaging outlives most of the terms people agree to.",
      },
    ],
    cpgIssues: [
      "A chef's or collaborator's recipe rarely scales as written — bench formulations change substantially in a commercial process, and the agreement should say who owns the scaled formulation as well as the original.",
      "The flavour house's own rights in a flavour base can sit underneath both parties' claims, so the collaboration ownership terms have to line up with the flavour house agreement.",
      "Limited-edition packaging is a full print minimum for a single run, so the packaging commitment is frequently larger than the product forecast justifies.",
      "Collaboration SKUs need their own item codes, specifications and shelf-life data, and retailers treat them as new items rather than as variants.",
      "The collaborator's name on the pack is a likeness licence with a term, and printed inventory outlives the term unless a sell-off is agreed.",
      "Allergen and certification profiles change with the collaborator's ingredient choices, so a collaboration can break a claim the base product carried.",
      "A sell-out is not automatically repeatable: booked line time, packaging lead times and the collaborator's availability all have to line up again, which is why the right to repeat needs to be pre-agreed.",
    ],
    founderMistakes: [
      "Developing a flavour with a chef, selling out the run, and having no right to make it again without renegotiating from a position of proven demand.",
      "Licensing collaboration artwork for a single run and losing the strongest can design the brand had produced.",
      "Committing a full label minimum for a limited edition and writing off most of it when the run under-sold.",
      "Never restricting what the collaborator could do next, and seeing a near-identical product appear with a competitor the following season.",
      "Agreeing a likeness term shorter than the time printed inventory took to sell through.",
      "Assuming a bench recipe would scale, then discovering the commercial version was a different product with an unclear owner.",
    ],
    negotiationPoints: [
      "Allocate ownership of the recipe, the scaled formulation and the artwork explicitly, with a licence back where the collaborator needs continued use.",
      "Pre-agree the terms of a repeat or extension so success does not trigger a fresh negotiation.",
      "Size the packaging commitment to a tested forecast, and agree who owns unsold inventory.",
      "Set a likeness term and sell-off period that outlast the expected sell-through of printed stock.",
      "Agree category restrictions on both sides for a defined period after the run.",
      "Confirm the collaboration's ingredient choices against every certification and allergen claim the base product carries.",
    ],
    redFlags: [
      "No allocation of ownership for the developed recipe or the artwork.",
      "Artwork licensed for a single run with no option to extend.",
      "A likeness term shorter than the expected life of printed inventory.",
      "No agreement on who owns unsold stock.",
      "No restriction on the collaborator doing the same thing with a competitor.",
    ],
    beforeYouSign: [
      "A written statement of who will own the recipe, the scaled formulation and the artwork.",
      "A costed production plan including the packaging minimum and a realistic sell-through case.",
      "Confirmation that the collaboration formulation holds every claim and certification on the base product.",
    ],
    related: [
      "co-branding-agreement",
      "collaboration-agreement",
      "formula-ownership-agreement",
      "product-development-ip-agreement",
      "trademark-license-agreement",
      "packaging-design-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default LICENSING_DOCS;
